import { PropertyKind } from "shared/types";
import {
  ApartmentsComVariant,
  Unit,
  ZillowVariant
} from "types/landlord/address";

export interface SyndicationState {
  zillow: boolean;
  zillowSpotlight: boolean;
  zillowVariant: ZillowVariant | null;
  apartmentsCom: boolean;
  apartmentsComVariant: ApartmentsComVariant | null;
  zumper: boolean;
  dwellsy: boolean;
  affordableHousingCom: boolean;
  rentalBeast: boolean;
  uloop: boolean;
  rentalSource: boolean;
  rentDuck: boolean;
  realtorCom: boolean;
}

interface MagicLinkSnapshot {
  linkIsActive: boolean;
  unitListed: boolean;
  syndication: SyndicationState;
}

interface MagicLinkPayload {
  active?: boolean;
  listed?: boolean;
  feed_zillow_enabled?: boolean;
  feed_zillow_spotlight_enabled?: boolean;
  feed_zillow_variant?: ZillowVariant | null;
  feed_apartments_com_enabled?: boolean;
  feed_apartments_com_variant?: ApartmentsComVariant | null;
  feed_zumper_enabled?: boolean;
  feed_dwellsy_enabled?: boolean;
  feed_affordable_housing_com_enabled?: boolean;
  feed_rental_beast_enabled?: boolean;
  feed_uloop_enabled?: boolean;
  feed_rental_source_enabled?: boolean;
  feed_rent_duck_enabled?: boolean;
  feed_realtor_com_enabled?: boolean;
}

const PROPERTY_KIND_TO_APARTMENTS_VARIANT: Record<
  string,
  ApartmentsComVariant
> = {
  [PropertyKind.SingleFamily]: "single_family",
  [PropertyKind.MultiFamily]: "multi_family",
  default: "single_family"
};

export const apartmentsVariantForKind = (
  propertyKind: PropertyKind | null
): ApartmentsComVariant =>
  PROPERTY_KIND_TO_APARTMENTS_VARIANT[propertyKind ?? "default"];

export const zillowVariantForKind = (
  propertyKind: PropertyKind | null | undefined
): ZillowVariant =>
  propertyKind === PropertyKind.MultiFamily ? "multi_family" : "single_family";

export const readSyndicationState = (
  unit: Unit,
  defaultVariant: ApartmentsComVariant,
  defaultZillowVariant: ZillowVariant
): SyndicationState => ({
  zillow: Boolean(unit.feed_zillow_enabled),
  zillowSpotlight: Boolean(unit.feed_zillow_spotlight_enabled),
  zillowVariant:
    unit.feed_zillow_variant ??
    (unit.feed_zillow_enabled ? defaultZillowVariant : null),
  apartmentsCom: Boolean(unit.feed_apartments_com_enabled),
  apartmentsComVariant:
    unit.feed_apartments_com_variant ??
    (unit.feed_apartments_com_enabled ? defaultVariant : null),
  zumper: Boolean(unit.feed_zumper_enabled),
  dwellsy: Boolean(unit.feed_dwellsy_enabled),
  affordableHousingCom: Boolean(unit.feed_affordable_housing_com_enabled),
  rentalBeast: Boolean(unit.feed_rental_beast_enabled),
  uloop: Boolean(unit.feed_uloop_enabled),
  rentalSource: Boolean(unit.feed_rental_source_enabled),
  rentDuck: Boolean(unit.feed_rent_duck_enabled),
  realtorCom: Boolean(unit.feed_realtor_com_enabled)
});

export const withZillowToggled = (
  state: SyndicationState,
  defaultVariant: ZillowVariant
): SyndicationState => {
  if (state.zillow)
    return {
      ...state,
      zillow: false,
      zillowSpotlight: false,
      zillowVariant: null
    };

  return {
    ...state,
    zillow: true,
    zillowVariant: state.zillowVariant ?? defaultVariant
  };
};

export const withZillowVariant = (
  state: SyndicationState,
  variant: ZillowVariant
): SyndicationState => ({ ...state, zillowVariant: variant });

export const withZumperToggled = (
  state: SyndicationState
): SyndicationState => ({
  ...state,
  zumper: !state.zumper
});

export const withDwellsyToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, dwellsy: !state.dwellsy });

export const withAffordableHousingComToggled = (
  state: SyndicationState
): SyndicationState => ({
  ...state,
  affordableHousingCom: !state.affordableHousingCom
});

export const withRentalBeastToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, rentalBeast: !state.rentalBeast });

export const withUloopToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, uloop: !state.uloop });

export const withRentalSourceToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, rentalSource: !state.rentalSource });

export const withRentDuckToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, rentDuck: !state.rentDuck });

export const withRealtorComToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, realtorCom: !state.realtorCom });

export const withApartmentsComToggled = (
  state: SyndicationState,
  defaultVariant: ApartmentsComVariant
): SyndicationState => {
  if (state.apartmentsCom)
    return { ...state, apartmentsCom: false, apartmentsComVariant: null };

  return {
    ...state,
    apartmentsCom: true,
    apartmentsComVariant: state.apartmentsComVariant ?? defaultVariant
  };
};

export const withApartmentsVariant = (
  state: SyndicationState,
  variant: ApartmentsComVariant
): SyndicationState => ({ ...state, apartmentsComVariant: variant });

const diffUloopPayload = (
  previous: SyndicationState,
  next: SyndicationState,
  payload: MagicLinkPayload
) => {
  if (previous.uloop !== next.uloop) payload.feed_uloop_enabled = next.uloop;
};

const diffRealtorComPayload = (
  previous: SyndicationState,
  next: SyndicationState,
  payload: MagicLinkPayload
) => {
  if (previous.realtorCom !== next.realtorCom)
    payload.feed_realtor_com_enabled = next.realtorCom;
};

const diffZillowPayload = (
  previous: SyndicationState,
  next: SyndicationState,
  payload: MagicLinkPayload
) => {
  if (previous.zillow !== next.zillow)
    payload.feed_zillow_enabled = next.zillow;

  const nextSpotlight = next.zillow && next.zillowSpotlight;
  const previousSpotlight = previous.zillow && previous.zillowSpotlight;
  if (previousSpotlight !== nextSpotlight)
    payload.feed_zillow_spotlight_enabled = nextSpotlight;

  const nextVariant = next.zillow ? next.zillowVariant : null;
  const previousVariant = previous.zillow ? previous.zillowVariant : null;
  if (previousVariant !== nextVariant)
    payload.feed_zillow_variant = nextVariant;
};

export const diffMagicLinkPayload = (
  previous: MagicLinkSnapshot,
  next: MagicLinkSnapshot
): MagicLinkPayload => {
  const payload: MagicLinkPayload = {};

  if (previous.linkIsActive !== next.linkIsActive) {
    payload.active = next.linkIsActive;
    payload.listed = next.unitListed;
  }
  if (previous.unitListed !== next.unitListed) payload.listed = next.unitListed;
  diffZillowPayload(previous.syndication, next.syndication, payload);

  if (previous.syndication.apartmentsCom !== next.syndication.apartmentsCom)
    payload.feed_apartments_com_enabled = next.syndication.apartmentsCom;

  const nextVariant = next.syndication.apartmentsCom
    ? next.syndication.apartmentsComVariant
    : null;
  const previousVariant = previous.syndication.apartmentsCom
    ? previous.syndication.apartmentsComVariant
    : null;
  if (previousVariant !== nextVariant)
    payload.feed_apartments_com_variant = nextVariant;

  if (previous.syndication.zumper !== next.syndication.zumper)
    payload.feed_zumper_enabled = next.syndication.zumper;

  if (previous.syndication.dwellsy !== next.syndication.dwellsy)
    payload.feed_dwellsy_enabled = next.syndication.dwellsy;

  if (
    previous.syndication.affordableHousingCom !==
    next.syndication.affordableHousingCom
  )
    payload.feed_affordable_housing_com_enabled =
      next.syndication.affordableHousingCom;

  if (previous.syndication.rentalBeast !== next.syndication.rentalBeast)
    payload.feed_rental_beast_enabled = next.syndication.rentalBeast;

  diffUloopPayload(previous.syndication, next.syndication, payload);

  if (previous.syndication.rentalSource !== next.syndication.rentalSource)
    payload.feed_rental_source_enabled = next.syndication.rentalSource;

  if (previous.syndication.rentDuck !== next.syndication.rentDuck)
    payload.feed_rent_duck_enabled = next.syndication.rentDuck;

  diffRealtorComPayload(previous.syndication, next.syndication, payload);

  return payload;
};
